/*
  Справочник судов с серверной пагинацией (21.10.2026, требование владельца: «пагинация
  должна быть серверной, а не на фронте»).

  Прежний ответ сервера нёс разом все суды с домами каждого и список домов без участка;
  экран резал его у себя. Теперь четыре чтения, и каждое отдаёт то, что показывает одна
  таблица, - страницу и общее число:

  - участки мировых судей и районные суды - две таблицы (`useCourtDirectory`);
  - дома выбранного участка (`useSectionBuildings`);
  - дома без участка с поиском по адресу - поиск идёт на сервере по ВСЕМУ списку
    (`useUnassignedHouses`).

  Перечитывание после любого действия (завели суд, привязали дом, внесли протокол) -
  одна цифра `version`: она входит в зависимости каждого чтения, и смена версии
  перечитывает все списки на тех же страницах, где человек их оставил.

  Выбор из перечня целиком (районный суд в строке участка, участок в мастере привязки)
  страниц не получает: это выпадающие списки справочника, им нужны все записи, и они
  берутся лёгким методом `courtSectionNames` (номер, наименование и вид, без домов и без
  разбора адресов портфеля).
*/

import { useCallback, useEffect, useState } from 'react'

import { api } from '../api/client.js'
import usePagedList from './usePagedList.js'

export const MAGISTRATE = 'magistrate'
export const DISTRICT = 'district'

export function useCourtDirectory(version) {
  // Версия входит в зависимости намеренно: смена версии - это «перечитай всё».
  /* eslint-disable react-hooks/exhaustive-deps */
  const loadMagistrates = useCallback(
    (window) => api.courtSections({ ...window, kind: MAGISTRATE }),
    [version],
  )
  const loadDistricts = useCallback(
    (window) => api.courtSections({ ...window, kind: DISTRICT }),
    [version],
  )
  /* eslint-enable react-hooks/exhaustive-deps */
  const magistrates = usePagedList(loadMagistrates)
  const districts = usePagedList(loadDistricts)

  const [summary, setSummary] = useState(null)
  const [blocked, setBlocked] = useState(null)
  const [choices, setChoices] = useState({ sections: [], districts: [] })
  const [error, setError] = useState(null)

  // `blocked` (02.11.2026) - по скольким домам заявления не соберутся: предупреждение
  // над вкладками. `summary` - дома без ВАШЕГО участка: число у списка привязки.
  useEffect(() => {
    let alive = true
    Promise.all([api.unassignedSummary(), api.courtSectionNames(), api.courtHousesSummary()])
      .then(([unassigned, names, houses]) => {
        if (!alive) return
        setSummary(unassigned)
        setBlocked(houses)
        setChoices({
          sections: names.filter((court) => court.kind !== DISTRICT),
          districts: names.filter((court) => court.kind === DISTRICT),
        })
      })
      .catch((failure) => {
        if (alive) setError(failure)
      })
    return () => {
      alive = false
    }
  }, [version])

  return { magistrates, districts, summary, blocked, choices, error }
}

export function useSectionBuildings(sectionId, version) {
  /* eslint-disable react-hooks/exhaustive-deps */
  const load = useCallback(
    (window) => api.courtSectionBuildings(sectionId, window),
    [sectionId, version],
  )
  /* eslint-enable react-hooks/exhaustive-deps */
  return usePagedList(load)
}

export function useUnassignedHouses(query, version) {
  /* eslint-disable react-hooks/exhaustive-deps */
  const load = useCallback(
    (window) => api.unassignedHouses({ ...window, q: query }),
    [query, version],
  )
  /* eslint-enable react-hooks/exhaustive-deps */
  return usePagedList(load)
}
